"use client";

import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from "react";
import { useTheme } from "next-themes";
import { useParams, useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeftIcon, Loader2, Calendar, Clock, Download, Filter, Search, MoreHorizontal, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { MobileMenuButton } from "@/components/ui/mobile-menu-button";
import { useUserSettings } from "@/contexts/user-settings-context";
import { usePlayer } from "@/contexts/player-context";
import { useUser } from "@/lib/context/user-context";
import { FollowButton } from '@/components/podcast/FollowButton';

interface Episode {
  shareId: string;
  title: string;
  author: string;
  ownerAvatarUrl: string;
  description: string;
  thumbnail: string;
  videoId: string;
  duration?: number;
  durationFormatted?: string;
  titleTranslation?: string;
  publishDate?: string;
}

interface ChannelData {
  id: string;
  name: string;
  avatar: string;
  episodes: Episode[];
  description?: string;
  topics?: string[];
  defaultLevelRange?: string[];
  backgroundColor?: string | null;
  thumbnail?: string | null;
}

const ENABLE_DIRECT_GLOBAL_PLAYER = true;

const decodeChannelSlug = (value: string): string => {
  if (!value) {
    return value;
  }

  let current = value;

  try {
    while (true) {
      const decoded = decodeURIComponent(current);
      if (decoded === current) {
        return decoded;
      }
      current = decoded;
    }
  } catch {
    return current;
  }
};

// Helper to parse duration string to seconds
const parseDurationToSeconds = (duration?: string): number => {
  if (!duration) return 0;
  
  const parts = duration.split(':').map(part => parseInt(part, 10));
  
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  } else {
    return parts[0] || 0;
  }
};

function EpisodeItem({ episode }: { episode: Episode }) {
  const episodeUrl = episode.videoId
    ? `/episodes/${episode.shareId}?videoId=${episode.videoId}`
    : `/episodes/${episode.shareId}`;
  const { userSettings } = useUserSettings();
  const { session } = useUser();
  const {
    setCurrentEpisode,
    setCleanReadData,
    setIsLoading: setPlayerLoading,
    openFullPlayer,
    closeFullPlayer,
  } = usePlayer();
  const router = useRouter();
  const preferChineseTitles = userSettings?.preferChineseTitles;
  const displayTitle =
    preferChineseTitles && episode.titleTranslation ? episode.titleTranslation : episode.title;
  const tooltipText = (() => {
    if (!episode.titleTranslation) return undefined;
    if (preferChineseTitles) return episode.title;
    return episode.titleTranslation;
  })();

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    // If it's a relative time string (e.g. "2 days ago", "1 year ago"), return as is
    if (dateString.includes('ago')) {
      return dateString;
    }
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isMobile = useIsMobile();
  const [isDirectLoading, setIsDirectLoading] = useState(false);
  const allowDirectPlay = ENABLE_DIRECT_GLOBAL_PLAYER && isMobile;

  const attemptDirectGlobalPlay = useCallback(async () => {
    if (!allowDirectPlay) {
      return false;
    }

    if (isDirectLoading) {
      return true;
    }

    try {
      setIsDirectLoading(true);
      openFullPlayer();
      setPlayerLoading(true);

      const shareId = episode.videoId || episode.shareId;
      const youtubeUrl = `https://www.youtube.com/watch?v=${shareId}`;

      const placeholderEpisode = {
        shareId: episode.shareId,
        sourceType: "youtube",
        videoId: shareId,
        videoUrl: youtubeUrl,
        origin: "video",
        title: episode.title,
        author: episode.author,
        thumbnail: episode.thumbnail,
        ownerAvatarUrl: episode.ownerAvatarUrl,
        hydrationPending: true,
        isHydrationPlaceholder: true,
        content: {
          shareId: episode.shareId,
          title: episode.title,
          author: episode.author,
          ownerAvatarUrl: episode.ownerAvatarUrl,
          thumbnail: episode.thumbnail,
          videoId: shareId,
          videoUrl: youtubeUrl,
          origin: "video",
          duration: episode.duration,
          hydrationPending: true,
          isHydrationPlaceholder: true,
        },
      };

      setCurrentEpisode(placeholderEpisode);
      setCleanReadData(null);
      setPlayerLoading(true);

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      const token =
        session?.access_token ??
        (typeof window !== "undefined" ? window.localStorage.getItem("token") : null);

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/episodes/${episode.shareId}`, {
        method: "GET",
        headers,
      });

      if (response.ok) {
      const episodeData = await response.json();
        const shareData = (episodeData?.content ?? {}) as Record<string, any>;
        const normalizedShare = {
          ...shareData,
          hydrationPending: false,
          isHydrationPlaceholder: false,
          content: {
            ...(shareData?.content ?? {}),
            hydrationPending: false,
            isHydrationPlaceholder: false,
          },
        };
        setCurrentEpisode(normalizedShare);
        setCleanReadData(
          normalizedShare?.content?.cleanRead ??
            shareData?.content?.cleanRead ??
            null,
        );
        return true;
      } else if (response.status === 404) {
        // Fallback for YouTube videos not yet in our DB
        // Don't fetch audio URL yet - let GlobalPlayerControls handle it after quota confirmation
        
        const durationSeconds = episode.duration ?? 0;
        
        const fallbackEpisode = {
          ...placeholderEpisode,
          duration: durationSeconds, // Ensure duration is passed as number
          hydrationPending: false,
          isHydrationPlaceholder: false,
          // Explicitly set audioUrl/videoUrl to undefined to trigger GlobalPlayer logic
          audioUrl: undefined, 
          content: {
            ...(placeholderEpisode.content || {}),
            duration: durationSeconds,
            hydrationPending: false,
            isHydrationPlaceholder: false,
            audioUrl: undefined,
          },
        };
        
        setCurrentEpisode(fallbackEpisode);
        setCleanReadData(null);
        return true; // Successfully handled, player is open (in waiting state)
      } else {
        throw new Error(`Failed to fetch episode data: ${response.status}`);
      }
    } catch (error) {
      console.error("Unable to open episode in global player:", error);
      closeFullPlayer();
      return false;
    } finally {
      setPlayerLoading(false);
      setIsDirectLoading(false);
    }
  }, [
    allowDirectPlay,
    closeFullPlayer,
    episode.author,
    episode.duration,
    episode.ownerAvatarUrl,
    episode.shareId,
    episode.thumbnail,
    episode.title,
    episode.videoId,
    isDirectLoading,
    openFullPlayer,
    session?.access_token,
    setCleanReadData,
    setCurrentEpisode,
    setPlayerLoading,
  ]);

  const handleCardClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!allowDirectPlay) {
        return;
      }

      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
        return;
      }

      e.preventDefault();

      const handled = await attemptDirectGlobalPlay();
      if (!handled) {
        // If not handled by global player (e.g. not mobile or error), navigate to episode page
        // But if it IS handled, we stay on the page and the player opens
        if (!allowDirectPlay) {
        if (typeof window !== "undefined") {
          window.location.href = episodeUrl;
        } else {
          router.push(episodeUrl);
           }
        }
      }
    },
    [allowDirectPlay, attemptDirectGlobalPlay, episodeUrl, router]
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          href={episodeUrl}
          onClick={handleCardClick}
          className="block w-full cursor-pointer group transition-all duration-200 hover:bg-primary/20 p-0 sm:p-1 rounded-lg"
        >
          {/* 水平卡片布局 */}
          <div className="flex gap-3 items-start">
            {/* 左侧缩略图 */}
            <div className="flex-shrink-0 w-[114px] h-[64px] rounded-md overflow-hidden relative self-start">
              <div className="w-full h-full relative">
                <Image
                  src={
                    episode.thumbnail ||
                    episode.ownerAvatarUrl ||
                    "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=128&h=80&fit=crop&crop=center"
                  }
                  alt={displayTitle}
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>

              {allowDirectPlay && isDirectLoading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                </div>
              )}

              {/* Duration badge - Bottom right corner */}
              {episode.durationFormatted && (
                <div className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                  {episode.durationFormatted}
                </div>
              )}
            </div>

            {/* 右侧内容 */}
            <div className="flex-1 min-w-0">
              {/* 顶部标题 */}
              <h3 className="text-sm leading-tight line-clamp-2 font-medium group-hover:text-foreground transition-colors duration-200">
                {displayTitle}
              </h3>

              {/* 底部频道信息 */}
              <div className="flex items-center gap-2 mt-1">
                {/* 作者头像 */}
                <div className="flex-shrink-0 w-5 h-5 rounded-full overflow-hidden bg-primary/15">
                  <Image
                    src={
                      episode.ownerAvatarUrl ||
                      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=20&h=20&fit=crop&crop=face"
                    }
                    alt={episode.author}
                    width={20}
                    height={20}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* 作者名称 & 发布时间 */}
                <div className="flex items-center gap-1 text-muted-foreground text-xs truncate">
                  <span className="group-hover:text-muted-foreground/80 transition-colors duration-200">
                  {episode.author}
                </span>
                  {episode.publishDate && (
                    <>
                      <span>•</span>
                      <span>{formatDate(episode.publishDate)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Link>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-normal">{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}

export default function ChannelPage() {
  const params = useParams();
  const channelId = params.id as string;
  const normalizedChannelId = decodeChannelSlug(channelId);
  const isTwoBrokeGirlsChannel = normalizedChannelId === "2_Broke_Girls";
  const [channelData, setChannelData] = useState<ChannelData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<"all" | number>("all");
  const { resolvedTheme } = useTheme();
  const isThemeDark = resolvedTheme === "dark";
  const isMobile = useIsMobile();
  const { session } = useUser(); // Get session for FollowButton
  const pageWrapperClass = cn(
    isMobile ? "overflow-x-hidden" : "sm:-mx-6 overflow-x-hidden",
    ""
  );
  const sectionPaddingClass = isMobile ? "px-2 pb-20" : "px-2 sm:px-6";
  const router = useRouter(); // Ensure router is available at top level scope if not already
  const { t } = useI18n();
  const seasonNumbers = useMemo(() => Array.from({ length: 6 }, (_, index) => index + 1), []);
  const [showStickyHeader, setShowStickyHeader] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyHeader(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  useEffect(() => {
    const fetchChannelData = async () => {
      if (!normalizedChannelId) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch(`/api/channel/${encodeURIComponent(normalizedChannelId)}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch channel: ${response.statusText}`);
        }
        
        const data = await response.json();
        setChannelData(data);
      } catch (err) {
        console.error("Error fetching channel data:", err);
        setError(err instanceof Error ? err.message : "Failed to load channel");
      } finally {
        setIsLoading(false);
      }
    };

    if (normalizedChannelId) {
      fetchChannelData();
    }
  }, [normalizedChannelId]);

  const filteredEpisodes = useMemo(() => {
    if (!channelData) {
      return [];
    }

    if (!isTwoBrokeGirlsChannel || selectedSeason === "all") {
      return channelData.episodes;
    }

    return channelData.episodes.filter((episode) => {
      const seasonMatch = episode.shareId.match(/S(\d{2})/i);
      if (!seasonMatch) {
        return false;
      }
      const seasonNumber = Number.parseInt(seasonMatch[1], 10);
      return seasonNumber === selectedSeason;
    });
  }, [channelData, isTwoBrokeGirlsChannel, selectedSeason]);

  const noEpisodesMessage = isTwoBrokeGirlsChannel && selectedSeason !== "all"
    ? t("channel.empty.seasonFiltered", "No episodes found for the selected season.")
    : t("channel.empty.generic", "This channel doesn't have any episodes yet.");

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center w-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground mt-4">Loading channel...</p>
              </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageWrapperClass}>
        {/* <ChannelHero> */}
          {/* <div className="mb-4 sm:mb-8 w-full sm:pt-4">
            <div className="flex justify-start">
              <Button
                variant="ghost"
                asChild
                className="text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm border border-white hover:border-white transition-all duration-200"
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}
              >
                <Link href="/">
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  <span style={{ textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
                    Back to Home
                  </span>
                </Link>
              </Button>
            </div>
          </div> */}
        {/* </ChannelHero> */}
        
        <div className={sectionPaddingClass}>
          <div className="text-center py-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Channel</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => window.location.reload()} className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all duration-200">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!channelData) {
    return (
      <div className={pageWrapperClass}>
        {/* <ChannelHero>
          <div className="mb-4 sm:mb-8 w-full sm:pt-4">
            <div className="flex justify-start">
              <Button
                variant="ghost"
                asChild
                className="text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm border border-white hover:border-white transition-all duration-200"
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}
              >
                <Link href="/">
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  <span style={{ textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
                    Back to Home
                  </span>
                </Link>
              </Button>
            </div>
          </div>
        </ChannelHero> */}
        
        <div className={sectionPaddingClass}>
          <div className="text-center py-8">
            <h1 className="text-2xl font-bold mb-4">Channel Not Found</h1>
            <p className="text-muted-foreground">The requested channel could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  const heroToneIsLight = false;
  const heroTextShadowStyle = heroToneIsLight ? undefined : { textShadow: "0 1px 3px rgba(0,0,0,0.3)" };
  const heroIconShadowStyle = heroToneIsLight ? undefined : { filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.3))" };

  const handleBack = () => {
    // Check if there is history to go back to
    if (window.history.length > 1) {
      router.back();
    } else {
      // Fallback to search page if no history
      router.push('/');
    }
  };

  return (
    <TooltipProvider>
      {/* <MobileMenuButton /> */}
      
      {/* Sticky Header */}
      <div 
        className={cn(
          "fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-md border-b transition-all duration-300 transform",
          showStickyHeader ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
        )}
        >
        <div className="container mx-auto px-4 h-14 flex items-center gap-4 max-w-7xl">
            <Button
              variant="ghost"
            size="icon"
            onClick={handleBack}
            className="hover:bg-transparent hover:text-primary shrink-0"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            </Button>
          
          {channelData && (
            <>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="relative w-8 h-8 rounded-full overflow-hidden border border-border/50 flex-shrink-0">
                <Image
                    src={channelData.avatar || "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=192&h=192&fit=crop&crop=center"}
                  alt={channelData.name}
                  fill
                  className="object-cover"
                />
              </div>
                <span className="font-bold truncate text-sm md:text-base">
                  {channelData.name}
                </span>
            </div>

              <div className="flex-shrink-0">
                <FollowButton
                  podcastId={channelData.id}
                  userId={session?.user?.id}
                  variant="default"
                  size="sm"
                  className="flex bg-foreground text-background items-center gap-2 h-8 rounded-xl"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="min-h-screen">
        <div className="container mx-auto px-2 sm:py-8 pb-8 max-w-7xl">
          <div className="mb-1">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              {t('common.back', 'Back')}
            </Button>
          </div>

          <div className="mb-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Channel Cover */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 md:w-48 md:h-48 rounded-xl overflow-hidden bg-gradient-to-br from-primary/20 to-primary/10 shadow-lg relative">
                  <Image
                    src={channelData.avatar || "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=192&h=192&fit=crop&crop=center"}
                    alt={channelData.name}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>

              {/* Channel Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0 mr-4">
                    <h1 className="text-2xl md:text-3xl font-bold mb-2 leading-tight">
                {channelData.name}
              </h1>
                    {/* Default Level Range */}
              {channelData?.defaultLevelRange && channelData.defaultLevelRange.length > 0 && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-muted-foreground">
                      Level Range:
                    </span>
                    <div className="flex items-center gap-1">
                      {channelData.defaultLevelRange.map((level, index) => (
                        <span
                          key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold border bg-muted/50 text-muted-foreground"
                        >
                          {level}
                        </span>
                      ))}
                  </div>
                </div>
              )}
                  </div>

                  {/* Action Buttons */}
                  {/* <div className="flex items-center gap-2 flex-shrink-0">
                    <FollowButton
                      podcastId={channelData.id}
                      userId={session?.user?.id}
                      variant="outline"
                      className="flex items-center gap-2"
                    />
                </div> */}
                </div>

                {/* Description */}
                {channelData?.description && (
                  <div className="text-muted-foreground text-sm md:text-base leading-relaxed line-clamp-3 mb-4">
                    {channelData.description}
                  </div>
                )}

                {/* Channel Stats */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-foreground">{channelData.episodes.length}</span>
                    <span>{t("channel.badges.episodes", "Episodes")}</span>
                  </div>
                  {channelData?.topics && channelData.topics.length > 0 && (
                    <div className="flex items-center gap-2 overflow-hidden">
                      {channelData.topics.slice(0, 3).map((topic, index) => (
                        <span key={index} className="bg-muted px-2 py-1 rounded-full text-xs">
                    {topic}
                  </span>
                ))}
              </div>
                  )}
            </div>
          </div>
              </div>
            </div>

          {/* Episodes List */}
          <div className="bg-background rounded-3xl shadow-sm border">
            {/* Header */}
            <div className="sm:p-4 p-2 border-b space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="sm:text-xl text-lg font-semibold">Episodes</h2>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Download
            </Button>
                  <Button variant="ghost" size="sm">
                    <Filter className="w-4 h-4" />
            </Button>
        </div>
              </div>

        {isTwoBrokeGirlsChannel && channelData.episodes.length > 0 && (
                <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <Button
                size="sm"
                variant={selectedSeason === "all" ? "default" : "outline"}
                onClick={() => setSelectedSeason("all")}
                className={cn(
                      "rounded-full text-xs h-8",
                      selectedSeason === "all" ? "shadow-sm" : "border-dashed"
                )}
              >
                {t("channel.filters.allSeasons", "All Seasons")}
              </Button>
              {seasonNumbers.map((seasonNumber) => {
                const isActive = selectedSeason === seasonNumber;
                return (
                  <Button
                    key={seasonNumber}
                    size="sm"
                    variant={isActive ? "default" : "outline"}
                    onClick={() => setSelectedSeason(seasonNumber)}
                    className={cn(
                          "rounded-full text-xs h-8 capitalize shrink-0",
                          isActive ? "shadow-sm" : "border-dashed"
                    )}
                  >
                        {t("channel.filters.season", "Season {season}").replace("{season}", String(seasonNumber))}
                  </Button>
                );
              })}
          </div>
        )}
            </div>

            {/* Content */}
            <div className="p-4">
        {filteredEpisodes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredEpisodes.map((episode) => (
              <div key={episode.shareId} className="w-full">
                <EpisodeItem episode={episode} />
              </div>
            ))}
          </div>
        ) : (
                <div className="text-center py-12">
                  <h3 className="text-lg font-medium mb-2">
              {t("channel.empty.title", "No Episodes Found")}
            </h3>
            <p className="text-muted-foreground">{noEpisodesMessage}</p>
          </div>
        )}
            </div>
          </div>
      </div>
      </div>
    </TooltipProvider>
  );
} 
